import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Check } from 'lucide-react';
import sixthLogo from '../assets/icon.svg';
import { local } from '../utils/constants';
import { PRICE_IDS, stripeMode, getPriceId } from '../utils/stripePriceIds';
import { upgradePlan, updateSubscriptionPaymentMethod } from '../apiLayer/dashBoardAPIs/vsCodeDashboardAPIs';
import { createWhopCheckout, createWhopLifetimeCheckout, upgradeWhopPlan } from '../apiLayer/dashBoardAPIs/whopAPIs';
import { fetchPaymentProviders, fetchUserProvider, getActiveProvider, resolvePlanId, WHOP } from '../utils/paymentProvider';
// TODO(lifetime-agreement): disabled until backend ships POST /payment/send_lifetime_invoice
// import LifetimeAgreementModal from '../components/LifetimeAgreementModal';
import '../styles/pricingRevamp.css';

const serverBaseUrl = local ? "http://127.0.0.1:8000" : "https://backend.withsix.co";

const PLAN_HIERARCHY = ['free_trial', 'pro', 'pro_plus', 'ultra', 'unlimited', 'lifetime'];

// Rolling promo deadline: Oct 5, 2026 23:59:59 UTC. Whenever it passes, it moves
// 5 days forward (Oct 10, Oct 15, ...), computed on every visit, so the countdown
// never shows more than 5 days and never sits at zero.
const PROMO_ANCHOR = Date.UTC(2026, 9, 5, 23, 59, 59); // months are 0-based: 9 = October
const PROMO_EXTENSION_MS = 5 * 24 * 60 * 60 * 1000;
const currentPromoEnd = (now = Date.now()) => {
  if (now < PROMO_ANCHOR) return new Date(PROMO_ANCHOR);
  const periods = Math.floor((now - PROMO_ANCHOR) / PROMO_EXTENSION_MS) + 1;
  return new Date(PROMO_ANCHOR + periods * PROMO_EXTENSION_MS);
};

/* ───────────────────────── Content (Pricing Revamp design) ───────────────────────── */

const FAQS = [
  { q: 'Is Unlimited actually unlimited?', a: 'Yes. Zero usage caps, zero rate limits, no fair-use throttling on the Unlimited Yearly and Lifetime plans. Use as many tokens as you want, on any frontier model, every day of the year. No other provider offers this at any price.' },
  { q: 'What happens when the launch-pricing timer ends?', a: 'Prices go up. Lifetime returns to $2,500, and the launch discounts across Pro, Pro+, and Ultra expire. Anything you buy before then is locked in: subscriptions keep their price for as long as you stay subscribed, and Lifetime is a single payment that never changes.' },
  { q: 'How is this so cheap?', a: "We've raised millions and built direct integrations with Anthropic, OpenAI, and Google — frontier models at scale with no reseller margins. We pass the savings straight to you, which is why we can sell unlimited for half of what others charge for a capped plan." },
  { q: 'Can I upgrade later? Do I lose what I paid?', a: "Never. Upgrade any time from this page — your current plan's full price is credited toward Unlimited or Lifetime, so you only pay the difference. Upgrades take effect instantly." },
  { q: 'Do you offer refunds?', a: 'Every paid plan comes with a 7-day money-back window. Beyond that, refunds are reviewed case-by-case based on usage during the billing period — we absorb significant model costs on every subscription.' },
];

const JUMP_CHIPS = [
  ['Free', 'plan-free'], ['Pro', 'plan-pro'], ['Pro+', 'plan-proplus'],
  ['Ultra', 'plan-ultra'], ['Unlimited', 'plan-unlimited'], ['Lifetime', 'lifetime-plan'],
];
const CARD_IDS = { Free: 'plan-free', Pro: 'plan-pro', 'Pro+': 'plan-proplus', Ultra: 'plan-ultra', Unlimited: 'plan-unlimited', Lifetime: 'lifetime-plan' };
const THEIR_WEEK = ['used', 'used', 'hit', 'locked', 'locked', 'locked', 'locked'];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const jumpTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' });
};

const pad2 = (n) => String(n).padStart(2, '0');
const fmtPrice = (v) => (v % 1 === 0 ? String(v) : v.toFixed(2).replace(/\.?0+$/, ''));
const fmtCents = (cents) => (cents / 100).toLocaleString('en-US', { maximumFractionDigits: cents % 100 ? 2 : 0 });

const Countdown = ({ t, seconds = true, sep = ' ' }) => (
  <>
    {t.days}d{sep}{pad2(t.hours)}h{sep}{pad2(t.minutes)}m{seconds ? <>{sep}{pad2(t.seconds)}s</> : null}
  </>
);

const Features = ({ items }) => (
  <div className="pr-features">
    {items.map((f, i) => (
      <div key={i} className="pr-feature"><span className="pr-tick">✓</span><span>{f}</span></div>
    ))}
  </div>
);

const ButtonLabel = ({ isLoading, state, fallback }) => {
  if (isLoading) return <span className="pr-spinner" />;
  const label = state?.label && state.action !== 'checkout' ? state.label : fallback;
  return <>{label}{!state?.disabled && ' →'}</>;
};

const TierCard = ({ plan, cycle, buttonState, isLoading, onAction }) => {
  const isFree = plan.id === 'Free';
  const yearly = cycle === 'yearly';
  let btnClass = 'pr-card-btn';
  if (isFree) btnClass += ' pr-btn-outline';
  if (plan.popular) btnClass += ' pr-btn-glow';
  return (
    <div id={CARD_IDS[plan.id]} className={`pr-card${plan.popular ? ' pr-card-popular' : ''}`}>
      {plan.popular && <span className="pr-popular-badge">✦ MOST POPULAR</span>}
      <div className="pr-card-name">{plan.name}</div>
      <div className="pr-card-desc">{plan.description}</div>
      {isFree ? (
        <>
          <div className="pr-price">$0</div>
          <div className="pr-price-note">forever · no card required</div>
        </>
      ) : (
        <>
          <div className="pr-price-row">
            <span className="pr-price"><span className="pr-price-cur">$</span>{fmtPrice(plan.price[cycle])}</span>
            <span className="pr-per">/mo</span>
          </div>
          <div className="pr-price-note">
            <span className="pr-was">${plan.originalPrice[cycle]}</span>
            <span className="pr-save">
              {yearly ? `billed $${plan.yearlyTotal}/yr` : `save $${(plan.originalPrice.monthly - plan.price.monthly) * 12}/yr`}
            </span>
          </div>
        </>
      )}
      <Features items={plan.features} />
      <button
        data-testid={`buy-${plan.id}`}
        className={btnClass}
        disabled={buttonState?.disabled || isLoading}
        onClick={() => !buttonState?.disabled && onAction(plan)}
      >
        <ButtonLabel isLoading={isLoading} state={buttonState} fallback={plan.cta} />
      </button>
    </div>
  );
};

const UnlimitedCard = ({ plan, quote, buttonState, isLoading, onAction }) => {
  const tone = plan.lifetime ? 'gold' : 'blue';
  const credited = quote && quote.credit_cents > 0;
  return (
    <div id={CARD_IDS[plan.id]} className={`pr-unl-card ${tone}`}>
      <div className="pr-shimmer" aria-hidden="true"><span /></div>
      <div className="pr-badges">
        {plan.lifetime ? (
          <>
            <span className="pr-badge gold">✦ BEST VALUE</span>
            <span className="pr-badge red">PRICE CUT — SAVE $750</span>
          </>
        ) : (
          <span className="pr-badge blue">◆ ONLY ON SIXTH</span>
        )}
      </div>
      <div className="pr-unl-name">{plan.name}</div>
      <p className="pr-unl-desc">{plan.description}</p>
      <div className="pr-unl-price-row">
        <span className="pr-unl-price">
          <span className="pr-unl-cur">$</span>
          {credited ? fmtCents(quote.final_cents) : plan.flatPrice.toLocaleString('en-US')}
        </span>
        {(credited || plan.wasPrice) && (
          <span className="pr-unl-was">${(credited ? plan.flatPrice : plan.wasPrice).toLocaleString('en-US')}</span>
        )}
        <span className="pr-unl-cadence">{plan.lifetime ? 'one payment · yours forever' : 'per year · cancel anytime'}</span>
      </div>
      {credited && (
        <div className="pr-credit-note">
          Your current plan's ${fmtCents(quote.credit_cents)} is credited — you pay the difference today.
        </div>
      )}
      <div className="pr-callout">
        {plan.lifetime ? (
          <>Less than <span className="pr-hl-gold">9 months</span> of a competitor's $200/mo plan — and it never expires. Returns to $2,500 when the timer ends.</>
        ) : (
          <>Competitors' best: <span className="pr-hl-danger">$2,400/yr</span> for a 20× cap that runs dry midweek. This is <span className="pr-hl-blue">half the price, no cap</span>.</>
        )}
      </div>
      <Features items={plan.features} />
      <button
        data-testid={`buy-${plan.id}`}
        className="pr-unl-btn"
        disabled={buttonState?.disabled || isLoading}
        onClick={() => !buttonState?.disabled && onAction(plan)}
      >
        <ButtonLabel isLoading={isLoading} state={buttonState} fallback={plan.cta} />
      </button>
      <div className="pr-fineprint">
        {plan.lifetime ? 'One payment, no subscription · secure checkout' : 'Billed yearly · secure checkout · 7-day money-back'}
      </div>
    </div>
  );
};

const UpgradeSuccessModal = ({ isOpen, onClose, planName }) => {
  if (!isOpen) return null;
  return (
    <div className="pr-modal-backdrop" onClick={onClose}>
      <div className="pr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pr-modal-icon"><Check size={26} strokeWidth={2.5} /></div>
        <h3>Upgrade successful!</h3>
        <p>You've been upgraded to <strong className="pr-strong">{planName}</strong>. Enjoy your new features.</p>
        <button className="pr-btn-accent" style={{ width: '100%', height: 46, fontSize: 14 }} onClick={onClose}>Continue →</button>
      </div>
    </div>
  );
};

/* ───────────────────────── Page ───────────────────────── */

const PricingPage = () => {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [upgradeLoading, setUpgradeLoading] = useState(null);
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [upgradedPlanName, setUpgradedPlanName] = useState('');
  const [timeLeft, setTimeLeft] = useState(null);
  const [providersInfo, setProvidersInfo] = useState(null);
  const [activeProvider, setActiveProviderState] = useState('stripe');
  // TODO(lifetime-agreement): disabled until backend ships POST /payment/send_lifetime_invoice
  // const [showLifetimeAgreement, setShowLifetimeAgreement] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // The user's OWN provider (stripe/whop), if they're already a subscriber. Drives
  // upgrade routing so existing Stripe subscribers keep upgrading on Stripe.
  const [userProvider, setUserProvider] = useState(null);

  // Load the backend provider config (enabled / default / allow_selection /
  // whop_sandbox) and pick the active provider.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const info = await fetchPaymentProviders();
      if (cancelled) return;
      setProvidersInfo(info);
      setActiveProviderState(getActiveProvider(info));
      // Detect the user's own provider (never throws / never redirects).
      const ownProvider = await fetchUserProvider();
      if (!cancelled && ownProvider) setUserProvider(ownProvider);
    })();
    return () => { cancelled = true; };
  }, []);

  const userPlanName = userInfo?.users_plan_name || 'free_trial';
  const userPlanIndex = PLAN_HIERARCHY.indexOf(userPlanName);

  // Paying subscribers get their current plan's full price credited when they move
  // to an Unlimited plan; ask the backend what they'd actually pay for each card.
  const [upgradeQuotes, setUpgradeQuotes] = useState({});
  useEffect(() => {
    const token = localStorage.getItem('jwt_token') || localStorage.getItem('web_access_token') || sessionStorage.getItem('jwt_token');
    if (!isAuthenticated || !token || !['pro', 'pro_plus', 'ultra', 'unlimited'].includes(userPlanName)) {
      setUpgradeQuotes({});
      return;
    }
    let cancelled = false;
    const targets = ['Unlimited', 'Lifetime'].filter((id) => !(id === 'Unlimited' && userPlanName === 'unlimited'));
    Promise.all(
      targets.map(async (id) => {
        const priceId = getPriceId(id, 'yearly');
        if (!priceId) return null;
        try {
          const res = await fetch(`${serverBaseUrl}/payment/upgrade_quote?price_id=${encodeURIComponent(priceId)}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          return res.ok ? [id, await res.json()] : null;
        } catch {
          return null;
        }
      }),
    ).then((entries) => {
      if (!cancelled) setUpgradeQuotes(Object.fromEntries(entries.filter(Boolean)));
    });
    return () => { cancelled = true; };
  }, [isAuthenticated, userPlanName]);

  // Plan data. ids / planKey / price fields drive checkout; copy follows the
  // Pricing Revamp design. Unlimited plans ignore the monthly/yearly toggle.
  const plans = [
    {
      id: 'Free', name: 'Free', planKey: 'free_trial',
      price: { monthly: 'Free', yearly: 'Free' },
      description: 'For curious developers',
      features: ['50 agent / chat requests per month', '2,000 code completions per month', 'Claude Sonnet 4.5 + Gemini 3', 'MCP, skills, hooks'],
      cta: 'Start free',
    },
    {
      id: 'Pro', name: 'Pro', planKey: 'pro',
      price: { monthly: 10, yearly: 8 }, originalPrice: { monthly: 20, yearly: 17 }, yearlyTotal: 100,
      description: 'Unlimited everyday coding',
      features: [<><strong className="pr-strong">Unlimited</strong> chat &amp; code completions</>, 'Extended agent limits, all frontier models', 'Image generation included', 'Code via Telegram on the go'],
      cta: 'Get started',
    },
    {
      id: 'Pro+', name: 'Pro+', planKey: 'pro_plus', popular: true,
      price: { monthly: 30, yearly: 22.5 }, originalPrice: { monthly: 60, yearly: 48 }, yearlyTotal: 270,
      description: 'Unlimited, 5× faster, everywhere',
      features: [<>Everything in Pro, <strong className="pr-strong">unlimited</strong> completions</>, '5× agent usage on Claude, GPT, Gemini', 'Priority queue — fastest responses', '24/7 dedicated support'],
      cta: 'Get started',
    },
    {
      id: 'Ultra', name: 'Ultra', planKey: 'ultra',
      price: { monthly: 100, yearly: 83 }, originalPrice: { monthly: 200, yearly: 160 }, yearlyTotal: 1000,
      description: 'For power users who never stop',
      features: [<>Everything in Pro+, <strong className="pr-strong">20× usage</strong> on all models</>, 'Early access to newest models', 'Dedicated infrastructure priority', 'White-glove onboarding & support'],
      cta: 'Go Ultra',
    },
    {
      id: 'Unlimited', name: 'Unlimited Yearly', planKey: 'unlimited', unlimited: true,
      flatPrice: 1250, price: { monthly: 1250, yearly: 1250 },
      description: 'A full year of Sixth with no limits of any kind.',
      features: [<><strong className="pr-strong">Zero usage caps</strong> — use as many tokens as you want</>, 'Never rate-limited, all year', 'Every frontier model + early access to new ones', 'Upgrading? Your current plan is credited in full'],
      cta: 'Go Unlimited',
    },
    {
      id: 'Lifetime', name: 'Unlimited for Life', planKey: 'lifetime', lifetime: true, unlimited: true,
      flatPrice: 1750, wasPrice: 2500, price: { monthly: 1750, yearly: 1750 },
      description: 'Pay once. Unlimited forever. Every future model included.',
      features: [<><strong className="pr-strong">Unlimited access for life</strong> — no caps, no rate limits, ever</>, 'Every frontier model + every future model', 'One payment — no subscription, no renewals', 'Upgrading? Your current plan is credited in full'],
      cta: 'Get Lifetime — save $750',
    },
  ];

  const getPlanButtonState = (plan) => {
    const planIndex = PLAN_HIERARCHY.indexOf(plan.planKey);
    if (!isAuthenticated) {
      if (plan.lifetime) {
        return { label: 'Get Lifetime', disabled: false, action: 'checkout' };
      }
      return { label: plan.id === 'Free' ? 'Start Free' : 'Get Started Now', disabled: false, action: 'checkout' };
    }
    // Lifetime is the top tier and a one-off purchase: a current lifetime owner
    // sees a disabled "Current plan"; every other plan (Free/Pro/Pro+/Ultra)
    // sees "Upgrade", which runs the one-off checkout redirect.
    if (plan.lifetime) {
      if (userPlanName === 'lifetime') {
        return { label: 'Current plan', disabled: true, action: 'none' };
      }
      return { label: 'Upgrade', disabled: false, action: 'upgrade' };
    }
    // Unlimited Yearly: lifetime owners already have it; paying subscribers switch
    // their subscription over (current plan credited); free users check out.
    if (plan.id === 'Unlimited') {
      if (userPlanName === 'unlimited') return { label: 'Current plan', disabled: true, action: 'none' };
      if (userPlanName === 'lifetime') return { label: 'Included in Lifetime', disabled: true, action: 'none' };
      if (userPlanName !== 'free_trial') return { label: 'Upgrade', disabled: false, action: 'upgrade' };
      return { label: 'Go Unlimited', disabled: false, action: 'checkout' };
    }
    if (planIndex <= userPlanIndex) {
      return { label: planIndex === userPlanIndex ? 'Current Plan' : 'Included', disabled: true, action: 'none' };
    }
    if (userPlanName !== 'free_trial') {
      return { label: 'Upgrade', disabled: false, action: 'upgrade' };
    }
    return { label: 'Subscribe', disabled: false, action: 'checkout' };
  };

  // Whop checkout (new purchase OR upgrade — Whop upgrades are a fresh hosted
  // checkout; the backend cancels the old membership on membership.activated).
  // Used for both handleGetStarted and handleUpgrade when the active provider
  // is Whop. Redirects to the returned hosted-checkout URL.
  const handleWhopCheckout = async (plan, billing, setLoading) => {
    const email = userInfo?.email || localStorage.getItem('user_email') || localStorage.getItem('email');
    const userId = localStorage.getItem('api_key');
    setLoading(plan.id);
    try {
      let result;
      if (plan.lifetime) {
        const successUrl = `${window.location.origin}/vscode/dashboard?lifetime=success`;
        result = await createWhopLifetimeCheckout({ email, user_id: userId, success_url: successUrl });
      } else {
        const planId = resolvePlanId(WHOP, plan.id, billing, providersInfo);
        if (!planId) {
          alert('This plan is not available on the selected payment provider yet.');
          return;
        }
        result = await createWhopCheckout({ plan_id: planId, email, user_id: userId });
      }
      const checkoutUrl = result?.url || result?.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        alert('Failed to create checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Whop checkout error:', error);
      alert('Failed to process. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  // Whop in-place upgrade: auto-charge the saved card the prorated difference and
  // switch the recurring plan. On success show the upgrade-success modal; if there
  // is no saved card or the charge declines, the backend returns a checkout URL to
  // fall back to. Lifetime stays a one-time checkout.
  const handleWhopUpgrade = async (plan, billing, setLoading) => {
    // Lifetime now goes through the same auto-charge upgrade path (charges the
    // lifetime price minus a credit for the current plan). The backend falls back
    // to a checkout URL for users with no membership/saved card (e.g. new buyers).
    const email = userInfo?.email || localStorage.getItem('user_email') || localStorage.getItem('email');
    const userId = localStorage.getItem('api_key');
    const planId = resolvePlanId(WHOP, plan.id, billing, providersInfo);
    if (!planId) {
      alert('This plan is not available on the selected payment provider yet.');
      return;
    }
    setLoading(plan.id);
    try {
      const result = await upgradeWhopPlan({ plan_id: planId, email, user_id: userId });
      if (result?.success) {
        // Auto-charge succeeded — in-place upgrade, no checkout.
        setUpgradedPlanName(plan.name);
        setShowSuccessModal(true);
        setUserInfo((prev) => ({ ...prev, users_plan_name: plan.planKey }));
        return;
      }
      const checkoutUrl = result?.checkout_url || result?.url;
      if (result?.requires_checkout && checkoutUrl) {
        // No saved card / declined — fall back to a hosted checkout.
        window.location.href = checkoutUrl;
        return;
      }
      alert(result?.message || 'Could not complete the upgrade. Please try again.');
    } catch (error) {
      console.error('Whop upgrade error:', error);
      alert('Failed to upgrade plan. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const handleUpgrade = async (plan) => {
    // Unlimited plans ignore the monthly/yearly toggle.
    const billing = plan.unlimited || billingCycle === 'yearly' ? 'yearly' : 'monthly';

    // Route by the user's OWN provider so existing Stripe subscribers keep
    // upgrading on Stripe (proration + auto-charge, no double-bill); existing Whop
    // subscribers on Whop. New users (no provider yet) fall back to the default.
    const upgradeVia = userProvider || activeProvider;

    // Whop upgrades try an in-place auto-charge first; checkout is the fallback.
    if (upgradeVia === WHOP) {
      return handleWhopUpgrade(plan, billing, setUpgradeLoading);
    }

    // getPriceId resolves the one-off id for Lifetime (which has no monthly/yearly
    // split) and the cycle-specific id for recurring tiers.
    const priceId = getPriceId(plan.id, billing);

    if (!priceId) {
      alert('Invalid plan selected.');
      return;
    }

    setUpgradeLoading(plan.id);

    try {
      const successUrl = plan.lifetime
        ? `${window.location.origin}/vscode/dashboard?lifetime=success`
        : undefined;
      const cancelUrl = plan.lifetime ? `${window.location.origin}/pricing` : undefined;
      const result = await upgradePlan(priceId, successUrl, cancelUrl);
      // Lifetime auto-debits the saved card like every other upgrade; the
      // backend only returns a hosted-checkout URL as the FALLBACK when the
      // charge fails (no card / declined) so the user can pay another way.
      if (result?.checkout_url) {
        window.location.href = result.checkout_url;
        return;
      }
      if (result?.fallback_update_payment_method_link) {
        window.location.href = result.fallback_update_payment_method_link;
        return;
      }
      if (result?.requires_payment_update) {
        // Declined with no chargeable invoice (e.g. no card on file) — open the
        // no-charge "update card" checkout; the webhook retries the upgrade after.
        try {
          const successUrl = `${window.location.origin}/pricing?card_updated=true`;
          const cancelUrl = `${window.location.origin}/pricing`;
          const r = await updateSubscriptionPaymentMethod(successUrl, cancelUrl);
          if (r?.checkout_url) {
            window.location.href = r.checkout_url;
            return;
          }
        } catch (e) {
          console.error('update payment method fallback failed:', e);
        }
        alert(result.message || 'Please update your payment method to upgrade.');
        return;
      }
      if (result?.success === false) {
        alert(result.message || 'Could not complete the upgrade. Please try again.');
        return;
      }
      setUpgradedPlanName(plan.name);
      setShowSuccessModal(true);
      setUserInfo((prev) => ({ ...prev, users_plan_name: plan.planKey }));
    } catch (error) {
      console.error('Upgrade error:', error);
      alert('Failed to upgrade plan. Please try again.');
    } finally {
      setUpgradeLoading(null);
    }
  };

  const handleGetStarted = async (plan) => {
    if (plan.id === 'Free') {
      navigate('/vscode/signup');
      return;
    }

    const buttonState = getPlanButtonState(plan);

    if (buttonState.action === 'upgrade') {
      handleUpgrade(plan);
      return;
    }

    const token = localStorage.getItem('jwt_token') || localStorage.getItem('web_access_token') || sessionStorage.getItem('jwt_token');

    if (!token) {
      const billing = plan.unlimited || billingCycle === 'yearly' ? 'yearly' : 'monthly';
      const returnUrl = encodeURIComponent(`/pricing?plan=${plan.id}&billing=${billing}`);
      navigate(`/vscode/signin?redirect=${returnUrl}`);
      return;
    }

    // Lifetime is a one-off purchase routed through the upgrade endpoint, which
    // returns a hosted-checkout URL to redirect to — not the recurring
    // create_checkout_url flow used by tiered plans.
    //
    // TODO(lifetime-agreement): purchase-agreement → email-invoice flow is
    // temporarily DISABLED pending the backend endpoint
    // (POST /payment/send_lifetime_invoice). To re-enable: restore the
    // LifetimeAgreementModal import/state/render and replace the branch below
    // with `setShowLifetimeAgreement(true); return;`.
    if (plan.lifetime) {
      handleUpgrade(plan);
      return;
    }

    // Whop: recurring plans go through a Whop hosted checkout.
    if (activeProvider === WHOP) {
      const billing = billingCycle === 'yearly' ? 'yearly' : 'monthly';
      return handleWhopCheckout(plan, billing, setCheckoutLoading);
    }

    setCheckoutLoading(plan.id);

    try {
      const billing = plan.unlimited || billingCycle === 'yearly' ? 'yearly' : 'monthly';
      const priceId = PRICE_IDS[stripeMode][plan.id]?.[billing];

      if (!priceId) {
        alert('Invalid plan selected.');
        return;
      }

      const apikey = localStorage.getItem('api_key');
      const email = userInfo?.email;

      const response = await fetch(`${serverBaseUrl}/payment/create_checkout_url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          price_id: priceId,
          payment_type: 'stripe',
          // Hardcoded 'stripe' (not activeProvider) on purpose: this branch IS the
          // Stripe checkout path — Whop returns early above, and we resolve a
          // Stripe price id here. Sending it explicitly stops the backend from
          // falling back to a Whop default and hijacking the checkout. A future
          // 3rd provider must get its own branch (like Whop), not ride this one.
          provider: 'stripe',
          user_id: apikey,
          email: email,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        const checkoutUrl = responseData.url || responseData.checkout_url;
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
        } else {
          alert('Failed to create checkout session. Please try again.');
        }
      } else {
        alert('Payment system error. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to process. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const currentQueries = location.search;
  const userEmail = localStorage.getItem('user_email') || localStorage.getItem('email') || userInfo?.email;
  const userInitial = userEmail ? userEmail.charAt(0).toUpperCase() : 'U';

  const checkAuthentication = async () => {
    try {
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('web_access_token') || localStorage.getItem('vscode_jwt_token') || sessionStorage.getItem('jwt_token');
      if (token) {
        setIsAuthenticated(true);
        try {
          const response = await fetch(`${serverBaseUrl}/auth/get_user_info`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          });
          if (response.ok) {
            const userData = await response.json();
            localStorage.setItem('api_key', userData.id);
            setUserInfo(userData);
          } else {
            localStorage.removeItem('jwt_token');
            localStorage.removeItem('web_access_token');
            setIsAuthenticated(false);
          }
        } catch (error) {
          console.warn('Token validation failed:', error);
        }
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    checkAuthentication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Promo countdown to the current rolling deadline (see currentPromoEnd).
  useEffect(() => {
    const tick = () => {
      const diff = currentPromoEnd().getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0, expired: true });
        return;
      }
      setTimeLeft({
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
        expired: false,
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Scroll to (and briefly emphasise) the Lifetime card when arriving at /pricing#lifetime
  useEffect(() => {
    if (location.hash !== '#lifetime') return undefined;
    const t = setTimeout(() => {
      const el = document.getElementById('lifetime-plan');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 350);
    return () => clearTimeout(t);
  }, [location.hash]);

  // Redirect-from-signin handler & error flash
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    if (errorParam) {
      let errorMessage = 'An error occurred';
      switch (errorParam) {
        case 'checkout_failed': errorMessage = 'Failed to create checkout session. Please try again.'; break;
        case 'payment_failed':  errorMessage = 'Payment system error. Please try again.'; break;
        case 'system_error':    errorMessage = 'System error occurred. Please try again later.'; break;
        default:                errorMessage = 'An unexpected error occurred.';
      }
      alert(errorMessage);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const planParam = urlParams.get('plan');
    const billingParam = urlParams.get('billing');
    if (planParam && ['Pro', 'Pro+', 'Ultra', 'Unlimited', 'Lifetime'].includes(planParam)) {
      if (billingParam === 'yearly') setBillingCycle('yearly');
      const token = localStorage.getItem('jwt_token') || localStorage.getItem('web_access_token');
      if (token) {
        const plan = plans.find((p) => p.id === planParam);
        if (plan) {
          handleGetStarted(plan);
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sticky countdown bar once the visitor has scrolled well past the plans.
  const [showSticky, setShowSticky] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > 1900);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const loadingPlan = checkoutLoading || upgradeLoading;
  const tierPlans = plans.filter((p) => !p.unlimited);
  const unlimitedPlans = plans.filter((p) => p.unlimited);
  const t = timeLeft || { days: 0, hours: 0, minutes: 0, seconds: 0 };
  const goPlans = () => jumpTo('pricing-cards');

  return (
    <div className="pr-page">
      {/* Urgency banner */}
      <div className="pr-urgency">
        <span style={{ fontWeight: 600 }}>Launch pricing ends soon — every plan goes up when the timer hits zero</span>
        <span className="pr-urgency-timer"><Countdown t={t} sep=" : " /></span>
        <span style={{ color: 'var(--pr-text-2)', whiteSpace: 'nowrap' }}>
          Lifetime returns to <span className="pr-strike">$2,500</span>
        </span>
      </div>

      {/* Nav */}
      <header className="pr-nav">
        <div className="pr-wrap pr-nav-inner">
          <div className="pr-brand">
            <Link to="/" className="pr-brand-link">
              <img src={sixthLogo} alt="Sixth AI" width={26} height={26} />
              <span className="pr-brand-name">Sixth</span>
            </Link>
            <Link to={`/pricing${currentQueries}`} className="pr-nav-current">Pricing</Link>
          </div>
          <div className="pr-nav-actions">
            {isAuthenticated ? (
              <>
                <Link to={`/vscode/dashboard${currentQueries}`} className="pr-nav-link">Dashboard</Link>
                <span className="pr-avatar">{userInitial}</span>
              </>
            ) : (
              <>
                <Link to={`/vscode/signin${currentQueries}`} className="pr-nav-link">Sign in</Link>
                <Link to={`/vscode/signup${currentQueries}`} className="pr-nav-cta">Get started →</Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pr-wrap pr-hero">
        <div className="pr-hero-pill">◆ New — Unlimited plans. No other AI coding tool offers this.</div>
        <h1>
          Unlimited AI coding.
          <br />
          <span className="pr-gradient-text">Everyone else sells caps.</span>
        </h1>
        <p>
          Every frontier model — Claude, GPT, Gemini — in your editor. Tiers from <span className="pr-strong">$8/mo</span>, truly
          unlimited from <span className="pr-strong">$1,250/yr</span>. Their best plan is $2,400/yr and runs out by Wednesday.
        </p>
        <div className="pr-hero-actions">
          <button className="pr-btn-accent" onClick={goPlans}>See all plans ↓</button>
          <span className="pr-hero-proof"><span className="pr-strong">500K+ developers</span> · ★★★★★</span>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing-cards" className="pr-wrap pr-pricing">
        <div className="pr-chips">
          {JUMP_CHIPS.map(([label, id]) => (
            <button key={id} className="pr-chip" onClick={() => jumpTo(id)}>{label}</button>
          ))}
        </div>
        <div className="pr-controls">
          <div className="pr-toggle">
            <button className={billingCycle === 'monthly' ? 'on' : ''} onClick={() => setBillingCycle('monthly')}>Monthly</button>
            <button className={billingCycle === 'yearly' ? 'on' : ''} onClick={() => setBillingCycle('yearly')}>
              Yearly <span className="pr-save-pill">−70%</span>
            </button>
          </div>
          <span className="pr-controls-note">Launch pricing · locked in for as long as you stay subscribed</span>
        </div>

        <div className="pr-tiers">
          {tierPlans.map((plan) => (
            <TierCard
              key={plan.id}
              plan={plan}
              cycle={billingCycle}
              buttonState={getPlanButtonState(plan)}
              isLoading={loadingPlan === plan.id}
              onAction={handleGetStarted}
            />
          ))}
        </div>

        {/* Ultra subscribers: top up credits */}
        {isAuthenticated && userPlanName === 'ultra' && (
          <div className="pr-credits">
            <div>
              <h4>Need more than your Ultra budget?</h4>
              <p>Top up credits any time. They're used only after your plan's budget runs out.</p>
            </div>
            <button className="pr-btn-accent" style={{ height: 42, fontSize: 13.5 }} onClick={() => navigate('/buy-credits')}>Buy credits →</button>
          </div>
        )}

        <div className="pr-unl-head">
          <span className="pr-unl-title">Go unlimited<span className="pr-serif" style={{ color: 'var(--pr-gold-text)' }}>.</span></span>
          <span className="pr-unl-sub">Zero caps. Zero rate limits. Nobody else sells this — at any price.</span>
        </div>
        <div className="pr-unl-grid">
          {unlimitedPlans.map((plan) => (
            <UnlimitedCard
              key={plan.id}
              plan={plan}
              quote={upgradeQuotes[plan.id]}
              buttonState={getPlanButtonState(plan)}
              isLoading={loadingPlan === plan.id}
              onAction={handleGetStarted}
            />
          ))}
        </div>

        <div className="pr-trust">
          <span><span className="pr-tick">✓</span>7-day money-back</span><span>·</span>
          <span><span className="pr-tick">✓</span>Cancel anytime</span><span>·</span>
          <span>Secure checkout with Stripe</span><span>·</span><span>Excludes tax</span>
        </div>
      </section>

      {/* Week comparison */}
      <section className="pr-wrap pr-section">
        <div className="pr-eyebrow">The unlimited difference</div>
        <h2 className="pr-week-title">
          Their "top tier" dies on <span className="pr-serif pr-danger-serif">Wednesday.</span>
        </h2>
        <p className="pr-week-lead">
          The best offer anywhere else is 20× usage for $200/mo — about $2,400 a year. Heavy users burn through it by midweek, then
          sit locked out until the week resets. Sixth Unlimited never stops.
        </p>
        <div className="pr-week">
          <div className="pr-week-row">
            <div className="pr-week-head">
              <span className="pr-week-label muted">Their $200/mo — 20× cap</span>
              <span className="pr-week-cost" style={{ color: 'var(--pr-danger)' }}>≈ $2,400/yr</span>
            </div>
            <div className="pr-days">
              {THEIR_WEEK.map((state, i) => (
                <div key={i} className={`pr-day ${state}`}>
                  {state === 'locked' ? 'locked' : DAYS[i]}
                  {state === 'hit' && <small>limit hit</small>}
                </div>
              ))}
            </div>
            <div className="pr-week-foot">Four days a week of paying for nothing. No top-ups, no alternatives — just waiting.</div>
          </div>
          <div className="pr-week-row">
            <div className="pr-week-head">
              <span className="pr-week-label">Sixth Unlimited — no cap</span>
              <span className="pr-week-cost" style={{ color: 'var(--pr-success)' }}>$1,250/yr · never stops</span>
            </div>
            <div className="pr-days">
              {DAYS.map((d) => <div key={d} className="pr-day open">{d}</div>)}
            </div>
            <div className="pr-week-foot">Every day. Every week. Every model. Half their price.</div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pr-wrap pr-section">
        <div className="pr-faq-grid">
          <div>
            <div className="pr-eyebrow">Questions</div>
            <h2 className="pr-faq-title">The fine <span className="pr-serif">print.</span></h2>
            <p className="pr-faq-lead">
              Don't see your question? <a href="mailto:ope@trysixth.com">Email us</a> — we answer within 4 hours.
            </p>
          </div>
          <div>
            {FAQS.map((f, i) => (
              <div key={f.q} className="pr-faq-item">
                <button className="pr-faq-q" onClick={() => setOpenFAQ(openFAQ === i ? -1 : i)}>
                  <span>{f.q}</span>
                  <span className="pr-faq-caret" style={{ transform: `rotate(${openFAQ === i ? 180 : 0}deg)` }}>▾</span>
                </button>
                <div className="pr-faq-a" style={{ maxHeight: openFAQ === i ? 320 : 0 }}>
                  <p>{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pr-wrap pr-final">
        <div className="pr-final-card">
          <div className="pr-final-pill">
            Launch pricing ends in <span className="pr-mono" style={{ fontWeight: 700 }}><Countdown t={t} /></span>
          </div>
          <h2>Unlimited won't stay<br />this <span className="pr-serif">cheap.</span></h2>
          <p>
            When the timer ends, Lifetime returns to $2,500 and launch discounts across every tier expire. Lock your price in now — it
            never goes up while you're subscribed.
          </p>
          <div className="pr-final-actions">
            <button className="pr-btn-accent" onClick={goPlans}>Lock in my price →</button>
            <button className="pr-btn-ghost" onClick={() => navigate('/vscode/signup')}>Start free — no card</button>
          </div>
        </div>
      </section>

      <footer className="pr-footer">
        <div className="pr-wrap pr-footer-inner">
          <span>© 2026 Sixth AI</span>
          <span className="pr-footer-links">
            <Link to="/terms">Terms</Link>
            <Link to="/privacy-policy">Privacy</Link>
            <Link to="/new_teams">Teams</Link>
          </span>
        </div>
      </footer>

      {/* Sticky countdown bar */}
      {showSticky && (
        <div className="pr-sticky">
          <span className="pr-sticky-text">
            Prices go up in <span className="pr-sticky-timer"><Countdown t={t} seconds={false} /></span>
          </span>
          <button onClick={goPlans}>See plans →</button>
        </div>
      )}

      <UpgradeSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        planName={upgradedPlanName}
      />
    </div>
  );
};

export default PricingPage;
